import React, {useEffect, useRef} from 'react'
import {useFormikContext} from 'formik'
import ComponentButton from '../ComponentButton'
import {Component} from '../../modules/ModuleForm'
import {TComponentObjectField, TTestResult, TTestResultQuestion} from './_types'
import {TModuleFormField, TModuleFormFieldSmartList} from '../../modules/ModuleForm/_types'
import {useIntl} from 'react-intl'
import {Col, Form} from 'react-bootstrap'
import ComponentTooltip from '../ComponentTooltip'
import ComponentAnnotation from '../ComponentAnnotation'

const ComponentObjectField: React.FC<TComponentObjectField> = ({article, properties}) => {
    const intl = useIntl()
    const {values, setFieldValue} = useFormikContext<TTestResult>()
    const value: TTestResultQuestion[] = (values as any)[article] ?? []
    const prevValueRef = useRef(JSON.stringify(value))
    const testType = (values as any)?.type?.value || (values as any)?.type

    useEffect(() => {
        const updated = [...value]
        let hasChanged = false

        value.forEach((row, rowIndex) => {
            if (
                (row?.type === 'single' || row?.type === 'multiple') &&
                (!row.answers || row.answers.length === 0)
            ) {
                updated[rowIndex].answers = [
                    {
                        id: -1,
                        row_id: row.id,
                        text: '',
                        isCorrect: false,
                        score: null,
                        isUserAnswer: '',
                        is_system: 'N',
                    },
                    {
                        id: -2,
                        row_id: row.id,
                        text: '',
                        isCorrect: false,
                        score: null,
                        isUserAnswer: '',
                        is_system: 'N',
                    },
                ]
                hasChanged = true
            }
        })

        const current = JSON.stringify(updated)
        if (prevValueRef.current !== current) {
            prevValueRef.current = current
            if (hasChanged) {
                setFieldValue(article, updated)
            }
        }
    }, [value])

    const getFilteredAnswersProps = (questionIndex: number): TModuleFormField[] => {
        const question = value[questionIndex]
        const type = question?.type

        const answersProp = properties.find((p) => p.article === 'answers') as
            | TModuleFormFieldSmartList
            | undefined

        if (!answersProp?.settings?.properties) return []

        return answersProp.settings.properties.filter((prop: TModuleFormField) => {
            if (type === 'range') {
                return ['from', 'to', 'score'].includes(prop.article)
            }

            if (type === 'text') {
                return ['text'].includes(prop.article)
            }

            if (testType === 'activity' || testType === 'survey') {
                return ['text', 'isCorrect', 'isUserAnswer'].includes(prop.article)
            }

            return ['text', 'score', 'isCorrect', 'isUserAnswer'].includes(prop.article)
        })
    }

    const handleAddRow = () => {
        const parentId = values.id
        const newRow = properties.reduce((acc, prop) => {
            acc[prop.article] = null
            return acc
        }, {} as Record<string, any>)

        newRow.row_id = parentId
        newRow.type = 'single'
        newRow.answers = [
            {text: '', isCorrect: false, score: null},
            {text: '', isCorrect: false, score: null},
        ]
        newRow.position = value.length // <-- присваиваем позицию

        setFieldValue(article, [...value, newRow])
    }

    const handleRemoveRow = (index: number) => {
        const updated = [...value]
        updated.splice(index, 1)
        setFieldValue(article, updated)
    }

    const moveRow = (fromIndex: number, toIndex: number) => {
        if (toIndex < 0 || toIndex >= value.length) return
        const updated = [...value]
        const [movedItem] = updated.splice(fromIndex, 1)
        updated.splice(toIndex, 0, movedItem)

        // Проставим позиции заново
        updated.forEach((item, index) => {
            item.position = index
        })

        setFieldValue(article, updated)
    }

    return (
        <div className='componentObjectField_tableWrapper'>
            <div className='table-responsive'>
                <table className='table table-row-dashed table-row-gray-300 gs-0 gy-4'>
                    <tbody>
                        {value
                            .filter((prop) => prop.is_visible !== false)
                            .map((row, rowIndex) => {
                                const visibleProps = properties.filter(
                                    (p) => p.is_visible !== false && p.article !== 'answers'
                                )
                                const propsColCount = Math.max(1, visibleProps.length) // защита от 0

                                return (
                                    <React.Fragment key={rowIndex}>
                                        {/* Строка с полями вопроса */}
                                        <tr>
                                            {/* левая колонка с контролами — на две строки */}
                                            {!values?.fieldsDisabled?.questions && (
                                                <td rowSpan={2} className='text-start align-middle'>
                                                    <div className='d-flex flex-column align-items-center justify-content-center gap-1'>
                                                        <ComponentButton
                                                            type='custom'
                                                            settings={{
                                                                icon: 'arrow-up',
                                                                title: 'Вверх',
                                                                background: 'light',
                                                            }}
                                                            customHandler={() =>
                                                                moveRow(rowIndex, rowIndex - 1)
                                                            }
                                                            defaultLabel='icon'
                                                        />
                                                        <div className='fw-bold text-muted text-center'>
                                                            {row.position != null
                                                                ? `${row.position + 1}`
                                                                : `${rowIndex + 1}`}
                                                        </div>
                                                        <ComponentButton
                                                            type='custom'
                                                            settings={{
                                                                icon: 'arrow-down',
                                                                title: 'Вниз',
                                                                background: 'light',
                                                            }}
                                                            customHandler={() =>
                                                                moveRow(rowIndex, rowIndex + 1)
                                                            }
                                                            defaultLabel='icon'
                                                        />
                                                    </div>
                                                </td>
                                            )}
                                            {/* поля вопроса (кроме answers) */}
                                            {visibleProps.map((prop) => (
                                                <td key={prop.article}>
                                                    {prop.title ? (
                                                        <Form.Group
                                                            className='moduleForm_field_labelContainer'
                                                            as={Col}
                                                            md={12}
                                                        >
                                                            <ComponentTooltip title={prop.title}>
                                                                <label
                                                                    className={`moduleForm_field_label${
                                                                        prop.is_required
                                                                            ? ' required'
                                                                            : ''
                                                                    }`}
                                                                >
                                                                    {prop.title}
                                                                </label>
                                                            </ComponentTooltip>
                                                            {prop.annotation ? (
                                                                <ComponentAnnotation
                                                                    annotation={prop.annotation}
                                                                />
                                                            ) : null}
                                                        </Form.Group>
                                                    ) : null}
                                                    <Form.Group
                                                        className='moduleForm_field_container'
                                                        as={Col}
                                                        md={12}
                                                    >
                                                        <div className='moduleForm_field_component'>
                                                            <Component
                                                                {...prop}
                                                                article={`${article}[${rowIndex}].${prop.article}`}
                                                            />
                                                        </div>
                                                    </Form.Group>
                                                </td>
                                            ))}
                                            {/* правая колонка с удалением — тоже на две строки */}
                                            {!values?.fieldsDisabled?.questions && (
                                                <td rowSpan={2} className='text-end align-middle'>
                                                    <div className='d-flex flex-column align-items-center justify-content-center gap-1'>
                                                        <ComponentButton
                                                            type='custom'
                                                            settings={{
                                                                icon: 'trash',
                                                                title: intl.formatMessage({
                                                                    id: 'BUTTON.DELETE',
                                                                }),
                                                                background: 'light',
                                                            }}
                                                            customHandler={() =>
                                                                handleRemoveRow(rowIndex)
                                                            }
                                                            defaultLabel='icon'
                                                        />
                                                    </div>
                                                </td>
                                            )}
                                        </tr>

                                        {/* Вторая строка: answers или заглушка */}
                                        {(() => {
                                            // if ((row?.type === 'text' || row?.type === 'range') && !values?.fieldsDisabled?.questions) {
                                            //   return (
                                            //       <tr>
                                            //         {/* только на ширину свойств (слева/справа уже rowSpan-ячейки) */}
                                            //         <td colSpan={propsColCount} className="text-center text-muted">
                                            //           {intl.formatMessage({ id: 'COMP_TEST.NO_ANSWERS' })}
                                            //         </td>
                                            //       </tr>
                                            //   )
                                            // }

                                            const smartProp = properties.find(
                                                (p) => p.article === 'answers'
                                            ) as TModuleFormFieldSmartList | undefined
                                            if (!smartProp) return null

                                            return (
                                                <tr>
                                                    <td colSpan={propsColCount}>
                                                        {row.type === 'text' ? (
                                                            <div className='px-4 py-2 border rounded bg-light'>
                                                                <div className='fw-semibold text-muted mb-1'>
                                                                    Ответ пользователя:
                                                                </div>
                                                                <div
                                                                    style={{
                                                                        wordBreak: 'break-word',
                                                                        whiteSpace: 'pre-wrap',
                                                                    }}
                                                                >
                                                                    {row?.answers?.[0]?.text ?? (
                                                                        <i>
                                                                            {intl.formatMessage({
                                                                                id: 'COMP_TEST.NO_ANSWERS_RESULT',
                                                                            })}
                                                                        </i>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ) : row?.type === 'range' ? (
                                                            <div className='px-4 py-2 border rounded bg-light'>
                                                                <div className='fw-semibold text-muted mb-1'>
                                                                    Ответ пользователя:
                                                                </div>
                                                                <div>
                                                                    {row?.answers?.[0]?.text ?? (
                                                                        <i>
                                                                            {intl.formatMessage({
                                                                                id: 'COMP_TEST.NO_ANSWERS_RESULT',
                                                                            })}
                                                                        </i>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <Component
                                                                {...smartProp}
                                                                article={`${article}[${rowIndex}].answers`}
                                                                isFieldVisible={true}
                                                                isFieldDisabled={false}
                                                                buttons={[]}
                                                                settings={{
                                                                    ...(smartProp.settings ?? {}),
                                                                    properties:
                                                                        getFilteredAnswersProps(
                                                                            rowIndex
                                                                        ),
                                                                }}
                                                            />
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        })()}
                                    </React.Fragment>
                                )
                            })}
                    </tbody>
                </table>
            </div>
            {!values?.fieldsDisabled?.questions && (
                <div className='mt-4'>
                    <ComponentButton
                        type='custom'
                        settings={{icon: 'plus', title: 'Добавить вопрос', background: 'dark'}}
                        customHandler={handleAddRow}
                    />
                </div>
            )}
        </div>
    )
}

export default ComponentObjectField
